import { useContext, useEffect, useState } from "react"

import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { usePointerPosition } from "shared/lib/drag-and-drop/usePointerPosition"
import { useEvent } from "shared/lib/useEvent"

const SCROLL_INTERVAL = 50 // milliseconds
const EDGE_DETECTION_ZONE = 44 // pixels from edge
const MAX_SCROLL_DISTANCE = 100 // pixels per interval (matches scrollDistance default)

// Cubic easing function for natural scrolling acceleration (from useDragScroll)
const ease = (n: number) => n ** 3 / 2

// Base speed calibrated so ease(2) * BASE_SPEED = MAX_SCROLL_DISTANCE
// ease(2) = 8/2 = 4, so BASE_SPEED = 80/4 = 20
const BASE_SCROLL_SPEED = MAX_SCROLL_DISTANCE / ease(2) // = 20

export interface UseTabBarScrollOnDragProps {
  enabled: boolean
  arrowLeftRef: React.RefObject<HTMLButtonElement | null>
  arrowRightRef: React.RefObject<HTMLButtonElement | null>
  scrollRef: React.RefObject<HTMLDivElement | null> // Access to scroll container
}

/**
 * Hook that auto-scrolls TabBar when dragging near scroll arrows or container edges.
 * Monitors drag state globally and triggers continuous scrolling with variable speed based on proximity.
 */
export function useTabBarScrollOnDrag({
  enabled,
  arrowLeftRef,
  arrowRightRef,
  scrollRef
}: UseTabBarScrollOnDragProps): void {
  const [scrollState, setScrollState] = useState<{
    direction: "left" | "right" | null
    distance: number // Distance to scroll in pixels
  }>({ direction: null, distance: 0 })

  // Monitor drag state and cursor position
  const { dragSource } = useContext(DragSourceDataContext)
  const { getPointerPosition } = usePointerPosition()
  const isDragging = !!dragSource

  const scrollByDistance = useEvent(
    (direction: "left" | "right", distance: number) => {
      if (!scrollRef.current) return

      const scrollAmount = direction === "left" ? -distance : distance
      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: "auto" // Use instant scrolling for smooth variable speed
      })
    }
  )

  const scroll = useEvent(() => {
    if (scrollState.direction && scrollState.distance > 0) {
      scrollByDistance(scrollState.direction, scrollState.distance)
    }
  })

  // Set up interval for continuous scrolling
  useEffect(() => {
    if (!scrollState.direction || !isDragging) return undefined

    const intervalId = setInterval(scroll, SCROLL_INTERVAL)

    return () => {
      clearInterval(intervalId)
    }
  }, [scrollState.direction, scrollState.distance, isDragging, scroll])

  // Position-based detection - check arrows first (priority), then edge proximity
  useEffect(() => {
    if (!isDragging || !enabled) {
      setScrollState({ direction: null, distance: 0 })
      return
    }

    const clientOffset = getPointerPosition()
    if (!clientOffset) {
      setScrollState({ direction: null, distance: 0 })
      return
    }

    const { x, y } = clientOffset

    // PRIORITY 1: Check if over arrow buttons (full speed)
    if (arrowLeftRef.current) {
      const leftRect = arrowLeftRef.current.getBoundingClientRect()
      if (
        x >= leftRect.left &&
        x <= leftRect.right &&
        y >= leftRect.top &&
        y <= leftRect.bottom
      ) {
        setScrollState({ direction: "left", distance: MAX_SCROLL_DISTANCE })
        return
      }
    }

    if (arrowRightRef.current) {
      const rightRect = arrowRightRef.current.getBoundingClientRect()
      if (
        x >= rightRect.left &&
        x <= rightRect.right &&
        y >= rightRect.top &&
        y <= rightRect.bottom
      ) {
        setScrollState({ direction: "right", distance: MAX_SCROLL_DISTANCE })
        return
      }
    }

    // PRIORITY 2: Check edge proximity (variable speed)
    if (!scrollRef.current) {
      setScrollState({ direction: null, distance: 0 })
      return
    }

    const scrollRect = scrollRef.current.getBoundingClientRect()

    // Check if cursor is within the scroll container vertically
    if (y < scrollRect.top || y > scrollRect.bottom) {
      setScrollState({ direction: null, distance: 0 })
      return
    }

    // Calculate distance from left edge
    const distanceFromLeft = x - scrollRect.left
    if (distanceFromLeft >= 0 && distanceFromLeft <= EDGE_DETECTION_ZONE) {
      // Near left edge - calculate speed with cubic easing
      const progress =
        (EDGE_DETECTION_ZONE - distanceFromLeft) / EDGE_DETECTION_ZONE
      const distance = ease(1 + progress) * BASE_SCROLL_SPEED
      setScrollState({ direction: "left", distance })
      return
    }

    // Calculate distance from right edge
    const distanceFromRight = scrollRect.right - x
    if (distanceFromRight >= 0 && distanceFromRight <= EDGE_DETECTION_ZONE) {
      // Near right edge - calculate speed with cubic easing
      const progress =
        (EDGE_DETECTION_ZONE - distanceFromRight) / EDGE_DETECTION_ZONE
      const distance = ease(1 + progress) * BASE_SCROLL_SPEED
      setScrollState({ direction: "right", distance })
      return
    }

    // Not in any scroll zone
    setScrollState({ direction: null, distance: 0 })
  }, [
    isDragging,
    enabled,
    getPointerPosition,
    arrowLeftRef,
    arrowRightRef,
    scrollRef
  ])
}
